import type { ActivityResponsesResult, ActivityStep, ClassActivity } from '../classroomApi';

// BP 22 / WIP-018 (design §1, §9.2) — the run-sheet action bar's primary-
// button rule, as a pure function. Everything zone C of RunSheetActionBar
// renders is derived from this: it takes the same `activity` / `responses`
// the page already polls (design §9.3 — "the bar adds no request") and
// returns exactly one of 'start' | 'show' | 'revote' | 'next' | 'stop' | null
// as the ONE primary action for the live step, per the table in design §9.2.
// `null` is itself a valid, tested state: an open poll with zero answers has
// no primary button at all (the hint lives in zone B instead).

export type LiveStepState = 'idle' | 'open' | 'shown';
export type PrimaryAction = 'start' | 'show' | 'revote' | 'next' | 'stop' | null;

export interface LivePhase {
  /** 'idle' when the activity itself is not live. */
  state: LiveStepState;
  step: ActivityStep | undefined;
  stepIndex: number | null;
  /** This step re-votes an earlier one (design §4 — WIP-010). */
  isRevote: boolean;
  /** Some other step in this activity re-votes THIS one. */
  hasRevote: boolean;
  answered: number;
  isLastStep: boolean;
  primary: PrimaryAction;
}

const IDLE: LivePhase = {
  state: 'idle', step: undefined, stepIndex: null,
  isRevote: false, hasRevote: false, answered: 0, isLastStep: false, primary: null,
};

// ── WIP-010 may edit ONLY this derivation (design §4.5, build order §2.3) ──
// A step's revote link is `revote_of` once content carries it. Until then,
// the fourteen authored `q*_v2` steps are inferred from the key suffix — this
// fallback is deleted the day the content commit lands.
// Exported (not just used below) so StepRow.tsx and index.tsx's student
// `LiveStep` can detect a revote step the same way, instead of a third
// hand-rolled copy of the same regex — a mirror of this exact derivation also
// lives in backend/src/services/summary/revote.js, since the two runtimes
// cannot share a module (design §4.5).
export function deriveRevoteOf(step: ActivityStep, steps: ActivityStep[]): string | undefined {
  if (step.revote_of) return step.revote_of;
  const m = /^(.+)_v2$/.exec(step.key);
  if (!m) return undefined;
  const v1Key = `${m[1]}_v1`;
  return steps.some((s) => s.key === v1Key) ? v1Key : undefined;
}
// ── end WIP-010 derivation ──────────────────────────────────────────────

function isPollStep(step: ActivityStep): boolean {
  return step.mode === 'poll' && !!step.options && step.options.length > 0;
}

export function livePhase(
  activity: ClassActivity | null | undefined,
  responses?: ActivityResponsesResult,
): LivePhase {
  if (!activity || !activity.is_live || activity.live_step == null) return IDLE;

  const stepIndex = activity.live_step;
  const step = activity.steps[stepIndex];
  if (!step) return IDLE;

  const isLastStep = stepIndex >= activity.steps.length - 1;
  const answered = responses?.summary?.[step.key]?.count ?? 0;
  const isRevote = !!deriveRevoteOf(step, activity.steps);
  const hasRevote = activity.steps.some((s) => deriveRevoteOf(s, activity.steps) === step.key);
  const state: LiveStepState = activity.results_shown ? 'shown' : 'open';
  const poll = isPollStep(step);

  let primary: PrimaryAction;
  if (state === 'open') {
    primary = poll ? (answered > 0 ? 'show' : null) : (isLastStep ? 'stop' : 'next');
  } else {
    primary = poll && !isRevote && !hasRevote ? 'revote' : (isLastStep ? 'stop' : 'next');
  }

  return { state, step, stepIndex, isRevote, hasRevote, answered, isLastStep, primary };
}
